import './collectibles.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LegacyAuthModalState, LoginType } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import { selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, initializeCustomer } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { useTranslation } from '../../../i18n';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { Collectible } from '../../models/collectibles.interface';
import { selectCollectibles, selectCollectiblesLoading } from '../../store/content.selectors';
import { loadCollectibles } from '../../store/content.thunk';

export interface CollectiblesProps {
  internalTitle?: string;
  titleSize?: string;
  description?: string;
  showCollectibleTitle: boolean;
  showMintNumber?: boolean;
  collectibleTitleSize: string;
  collectibleTitleAlignment: string;
  showLogin: boolean;
  loggedIn: boolean;
  maxWidth: boolean;
  // CSS Variables
  textColor: string;
  sectionBackgroundColor?: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  buttonBorderColor: string;
  imageBorderColor: string;
  hoverBackgroundColor: string;
  columns: string;
  imageBorderRadius: string;
  imageBorderWidth: string;
  buttonBorderRadius: string;
  buttonBorderWidth: string;
  horizontalPadding: string;
  verticalPadding: string;
}

const CollectiblesContent: FunctionComponent<CollectiblesProps> = ({
  internalTitle,
  titleSize,
  description,
  showCollectibleTitle,
  showMintNumber,
  collectibleTitleSize,
  collectibleTitleAlignment,
  showLogin,
  loggedIn,
  maxWidth,
  textColor,
  sectionBackgroundColor,
  buttonBackgroundColor,
  buttonTextColor,
  buttonBorderColor,
  imageBorderColor,
  hoverBackgroundColor,
  columns,
  imageBorderRadius,
  imageBorderWidth,
  buttonBorderRadius,
  buttonBorderWidth,
  horizontalPadding,
  verticalPadding,
}) => {
  const cssVars = {
    '--textColor': textColor,
    '--sectionBackgroundColor': sectionBackgroundColor,
    '--buttonBackgroundColor': buttonBackgroundColor,
    '--buttonTextColor': buttonTextColor,
    '--buttonBorderColor': buttonBorderColor,
    '--imageBorderColor': imageBorderColor,
    '--hoverBackgroundColor': hoverBackgroundColor,
    '--columns': columns,
    '--imageBorderRadius': imageBorderRadius,
    '--imageBorderWidth': imageBorderWidth,
    '--buttonBorderRadius': buttonBorderRadius,
    '--buttonBorderWidth': buttonBorderWidth,
    '--horizontalPadding': horizontalPadding,
    '--verticalPadding': verticalPadding,
  };

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectCollectiblesLoading);
  const collectiblesResp = useAppSelector(selectCollectibles);
  const { collectibles } = collectiblesResp || {};

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());
    dispatch(loadCollectibles());
  }, [dispatch]);

  const stripMintNumber = (value: string) => {
    return value.replace(/\s*#\d+\s*$/, '').trim();
  };

  const mintNumberOnly = (value: string) => {
    const regex = /#\s*(\d+)\s*(?=\D|$)/g;
    const matches = value.match(regex);

    return matches ? matches[matches.length - 1].trim() : null;
  };

  const collectibleCardStyle = (collectible: Collectible) => {
    if (collectible.animationUri) {
      return { 'background-color': 'black' };
    }
    return { 'background-image': 'url(' + collectible.imageUri + ')' };
  };

  const openLoginModal = async () => {
    await authenticationFlowService.initiateAuthentication({ state: LegacyAuthModalState.LOGIN });
  };

  return (
    <div class="single-collectibles" style={cssVars}>
      <div class={`collectibles-wrapper ${maxWidth ? 'page-width' : ''}`}>
        {loading && (
          <section class="single-loading">
            <SpinnerComponent />
          </section>
        )}

        {isAuthenticated && collectibles && (
          <div class="collectibles-list-wrapper">
            <div class="collectibles-header">
              {!!internalTitle && internalTitle !== '' && (
                <h2 class={`single-product-title ${titleSize}`}>
                  <span>{internalTitle}</span>
                </h2>
              )}

              {!!description && description !== '' && (
                <div class="description" dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </div>

            <div class="single-card-grid">
              {collectibles.map((collectible, index) => (
                <div key={index} class="single-card-collectible">
                  {collectible.imageUri && (
                    <div class="single-card-image" style={collectibleCardStyle(collectible)}>
                      {collectible.animationUri && (
                        <video
                          class="single-collectible-video"
                          autoPlay
                          loop
                          muted
                          playsInline
                          onCanPlay={(e) => e.target.play()}
                          poster={collectible.imageUri}
                          onLoadedMetadata={(e) => {
                            e.target.muted = true;
                            e.target.playsInline = true;
                          }}
                        >
                          <source src={collectible.animationUri} type="video/mp4" />
                        </video>
                      )}

                      <div class="btn-overlay">
                        <a
                          href={collectible.shareUri}
                          target="_blank"
                          rel="noreferrer"
                          class="btn button"
                        >
                          {t('perks.view')}
                        </a>
                        {collectible.claimUri && (
                          <a
                            href={collectible.claimUri}
                            target="_blank"
                            rel="noreferrer"
                            class="btn button"
                          >
                            {t('perks.transfer')}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {(showCollectibleTitle || showMintNumber) && (
                    <div class="single-card-inner">
                      <h3 class={collectibleTitleSize}>
                        {showCollectibleTitle ? (
                          <span>
                            {showMintNumber ? collectible.name : stripMintNumber(collectible.name)}
                          </span>
                        ) : (
                          <span>{mintNumberOnly(collectible.name)}</span>
                        )}
                      </h3>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isAuthenticated && showLogin && (
          <div class="single-login-container">
            <button onClick={openLoginModal} class="button btn">
              {t('perks.logInToViewCollectibles')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const CollectiblesComponent: FunctionComponent<CollectiblesProps> = (props) => {
  return (
    <Provider store={store}>
      <CollectiblesContent {...props} />
    </Provider>
  );
};
